import { 
  Directive, 
  Input, 
  OnInit, 
  OnDestroy, 
  ElementRef, 
  Renderer2, 
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MedalsService } from '../services/medals.service';

@Directive({
  selector: '[appUserMedal]',
  standalone: true
})
export class UserMedalDirective implements OnInit, OnDestroy, OnChanges {
  @Input('appUserMedal') userId: string | null = null;
  @Input() medalSize: 'small' | 'normal' | 'large' = 'normal';
  
  private destroy$ = new Subject<void>();
  private wrapperElement: HTMLElement | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private medalsService: MedalsService
  ) {}

  ngOnInit(): void {
    this.setupMedalBadge();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId'] && !changes['userId'].isFirstChange()) {
      this.setupMedalBadge();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanup();
  }

  private setupMedalBadge(): void {
    if (!this.userId) {
      this.cleanup();
      return;
    }

    // Create wrapper if needed
    if (!this.wrapperElement) {
      this.createWrapper();
    }

    // Fetch medal data
    this.medalsService.getUserMedal(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(medal => {
        if (medal && medal.active) {
          this.showMedalBorder(medal.medal);
        } else {
          this.hideMedalBorder();
        }
      });
  }

  private createWrapper(): void {
    const parent = this.el.nativeElement.parentElement;
    
    // Create wrapper div
    this.wrapperElement = this.renderer.createElement('div');
    this.renderer.addClass(this.wrapperElement, 'avatar-medal-wrapper');
    this.renderer.setStyle(this.wrapperElement, 'position', 'relative');
    this.renderer.setStyle(this.wrapperElement, 'display', 'inline-block');
    
    // Apply size class
    if (this.medalSize !== 'normal') {
      this.renderer.addClass(this.wrapperElement, `${this.medalSize}-avatar`);
    }
    
    // Insert wrapper and move image inside
    this.renderer.insertBefore(parent, this.wrapperElement, this.el.nativeElement);
    this.renderer.appendChild(this.wrapperElement, this.el.nativeElement);
  }

  private showMedalBorder(medal: 'GOLD' | 'SILVER' | 'BRONZE'): void {
    // Add medal border class to wrapper
    if (this.wrapperElement) {
      this.renderer.addClass(this.wrapperElement, 'has-medal');
      this.renderer.addClass(this.wrapperElement, `medal-border-${medal.toLowerCase()}`);
    }
  }

  private hideMedalBorder(): void {
    // Remove medal border classes from wrapper
    if (this.wrapperElement) {
      this.renderer.removeClass(this.wrapperElement, 'has-medal');
      this.renderer.removeClass(this.wrapperElement, 'medal-border-gold');
      this.renderer.removeClass(this.wrapperElement, 'medal-border-silver');
      this.renderer.removeClass(this.wrapperElement, 'medal-border-bronze');
    }
  }

  private cleanup(): void {
    this.hideMedalBorder();
    
    // Remove wrapper and restore original structure
    if (this.wrapperElement && this.el.nativeElement) {
      const parent = this.wrapperElement.parentElement;
      if (parent) {
        this.renderer.insertBefore(parent, this.el.nativeElement, this.wrapperElement);
        this.renderer.removeChild(parent, this.wrapperElement);
      }
      this.wrapperElement = null;
    }
  }
}